import { useCallback, useEffect, useMemo } from "react";

import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";

import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";

import localSettings from "@app/config/LocalSettings";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import useAnalytics from "@app/util/contexts/useAnalytics";

import getTabDisplayName from "../CompanyRecordPane/Content/getTabDisplayName";

interface Params {
  activeTab: RecordPaneTab | undefined;
  entity: Company | Deal | Person | undefined;
  onActiveTabChange: (tab: RecordPaneTab) => void;
}

const useTabs = ({ activeTab, entity, onActiveTabChange }: Params) => {
  const intl = useIntl();
  const history = useHistory();
  const { pathname } = useLocation();
  const analytics = useAnalytics();

  const defaultTabsOrder = useMemo(
    () => (entity?.entity ? localSettings.getRecordTabsOrder(entity.entity) : []),
    [entity?.entity]
  );

  const activeTabByUrl = useMemo(
    () => (pathname.length > 1 ? (pathname.split("/")[1] as RecordPaneTab) : undefined),
    [pathname]
  );

  const defaultTabRedirect = activeTab ?? defaultTabsOrder[0];

  useEffect(() => {
    if (activeTabByUrl && activeTab !== activeTabByUrl) {
      onActiveTabChange(activeTabByUrl);
    }
  }, [activeTab, activeTabByUrl, onActiveTabChange]);

  const handleTabsChange = useCallback(
    (tab: RecordPaneTab) => {
      history.push(`/${tab}`);

      const tabDisplayName = getTabDisplayName(intl, tab);
      analytics.clicked([tabDisplayName], { entity });
    },
    [analytics, entity, intl, history]
  );

  const handleTabsOrderChange = useCallback(
    (tabsOrder: RecordPaneTab[]) => {
      localSettings.setRecordTabsOrder(entity!.entity, tabsOrder);
    },
    [entity]
  );

  return {
    defaultTabRedirect,
    defaultTabsOrder,
    handleTabsChange,
    handleTabsOrderChange,
  };
};

export default useTabs;
